import { type AuditMechanicalFormEntry } from '#packages/data-collection/models/audit-mechanical-form';

import {
  SystemLabel,
  RatingUnitLabel,
  SourceLabel,
  FuelTypeLabel,
} from '#apps/client-web/features/audit-form-mechanical/constants';

import { type ExcelTableMapping } from '../types/excel-mapping';

/**
 * Maps Mechanical form entries to the "2. Mech" Excel sheet.
 *
 * Each entry occupies one row starting at row 29.
 * Column letters correspond to the template table headers.
 */
export const mechanicalTableMapping: ExcelTableMapping<AuditMechanicalFormEntry> = {
  label: 'Mechanical',
  startRangeName: 'mech_data_start',

  columns: [
    /* A – Order (auto-numbered) */
    {
      column: 'A',
      getValue: (_entry, index) => index + 1,
    },
    /* B – System */
    {
      column: 'B',
      getValue: (e) => (e.system ? SystemLabel[e.system] : undefined),
    },
    /* C – Location */
    {
      column: 'C',
      getValue: (e) => e.location,
    },
    /* D – Service area or equipment */
    {
      column: 'D',
      getValue: (e) => e.serviceAreaOrEquipment,
    },
    /* E – Type */
    {
      column: 'E',
      getValue: (e) => e.type,
    },
    /* F – Tag */
    {
      column: 'F',
      getValue: (e) => e.tag,
    },
    /* G – Qty */
    {
      column: 'G',
      getValue: (e) => e.quantity,
    },
    /* H – Make */
    {
      column: 'H',
      getValue: (e) => e.make,
    },
    /* I – Model */
    {
      column: 'I',
      getValue: (e) => e.model,
    },
    /* J – Power rating */
    {
      column: 'J',
      getValue: (e) => {
        if (!e.powerRating) return undefined;
        const num = parseFloat(e.powerRating);
        return isNaN(num) ? e.powerRating : num;
      },
    },
    /* K – Rating unit */
    {
      column: 'K',
      getValue: (e) => (e.ratingUnit ? RatingUnitLabel[e.ratingUnit] : undefined),
    },
    /* L – Fuel Type */
    {
      column: 'L',
      getValue: (e) => (e.fuelType ? FuelTypeLabel[e.fuelType] : undefined),
    },
    /* M – Rating source */
    {
      column: 'M',
      getValue: (e) => (e.ratingSource ? SourceLabel[e.ratingSource] : undefined),
    },
    /* N – Efficiency */
    {
      column: 'N',
      getValue: (e) => (e.efficiency != null ? e.efficiency / 100 : undefined),
    },
    /* O – Efficiency source */
    {
      column: 'O',
      getValue: (e) => (e.efficiencySource ? SourceLabel[e.efficiencySource] : undefined),
    },
    /* P – Ventilation Rate (CFM) */
    {
      column: 'P',
      getValue: (e) => e.ventilationRate,
    },
    /* Q – Serial number */
    {
      column: 'Q',
      getValue: (e) => e.serialNumber,
    },
    /* R – YYYY/MM of manufacture */
    {
      column: 'R',
      getValue: (e) => e.manufactureDate?.format('YYYY/MM'),
    },
    /* S – Hours */
    {
      column: 'S',
      getValue: (e) => e.hours,
    },
    /* T – Control */
    {
      column: 'T',
      getValue: (e) => e.control,
    },
    /* U – Condition (not in form schema) */
    {
      column: 'U',
      getValue: () => undefined,
    },
    /* V – Other */
    {
      column: 'V',
      getValue: (e) => e.other,
    },
    /* W – Data Collection App Photo IDs */
    {
      column: 'W',
      getValue: (e) => e.attachments?.map((a) => a.name).join(', '),
    },
  ],
};
